import { isNativeApp } from "@/lib/nativeBridge";
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BellRingingIcon as BellRinging, XIcon as CloseIcon, HeartIcon as Heart } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

/** Settings card for Buyer-Pass breed-alert preferences.
 *
 *  A chips-style multi-select. Users type a breed, pick from the
 *  autocomplete (populated by /api/public/breeds) or press Enter to
 *  add a custom one. Selected breeds render as removable chips. The
 *  whole card is gated on an active Buyer Pass — non-Pass viewers see
 *  a locked state with an "Unlock Buyer Pass" CTA.
 */
export default function BreedAlertsCard() {
  const [hasPass, setHasPass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [breeds, setBreeds] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get("/me/buyer-pass").then((r) => r.data?.active).catch(() => false),
      api.get("/me/breed-alerts").then((r) => r.data?.breeds || []).catch(() => []),
      api.get("/public/breeds").then((r) => r.data?.breeds || []).catch(() => []),
    ]).then(([pass, mine, all]) => {
      setHasPass(!!pass);
      setBreeds(mine);
      setSuggestions(all);
    }).finally(() => setLoading(false));
  }, []);

  const lower = useMemo(() => new Set(breeds.map((b) => b.toLowerCase())), [breeds]);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return suggestions.filter((s) => s.toLowerCase().includes(q) && !lower.has(s.toLowerCase())).slice(0, 6);
  }, [query, suggestions, lower]);

  const addBreed = (raw) => {
    const next = (raw || "").trim();
    if (!next) return;
    if (lower.has(next.toLowerCase())) {
      setQuery("");
      return;
    }
    if (breeds.length >= 20) {
      toast.error("Max 20 breeds on the alert list.");
      return;
    }
    setBreeds([...breeds, next]);
    setQuery("");
    inputRef.current?.focus();
  };

  const removeBreed = (name) => setBreeds(breeds.filter((b) => b !== name));

  const onInputKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (matches.length > 0) addBreed(matches[0]);
      else addBreed(query);
    } else if (e.key === "Backspace" && !query && breeds.length > 0) {
      // Delete the last chip on backspace-in-empty-input — matches the
      // muscle-memory of email/tag inputs everywhere.
      removeBreed(breeds[breeds.length - 1]);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/breed-alerts", { breeds });
      setBreeds(data.breeds);
      toast.success("Breed alerts saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mb-8 bg-white border border-[#E5E5E5] rounded-xl p-6 text-sm text-[#6B6B6B]">
        Loading breed alerts…
      </div>
    );
  }

  // Non-Pass: gentle lock card pointing to /billing.
  if (!hasPass) {
    return (
      <div
        className="mb-8 bg-gradient-to-br from-[#FBF7EC] to-white border border-[#E8D9A6] rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.04)] overflow-hidden"
        data-testid="breed-alerts-locked-card"
      >
        <div className="px-6 py-5 border-b border-[#E8D9A6]/60 flex items-center gap-2">
          <BellRinging size={20} weight="duotone" className="text-gold" />
          <h2 className="font-spectral text-xl">Breed alerts</h2>
          {!isNativeApp() && (
          <span className="text-[10px] bg-[#C9A227]/20 text-[#8a6f10] border border-[#C9A227]/40 rounded-full px-2 py-0.5 font-bold uppercase tracking-[0.15em]">
            Buyer Pass
          </span>
          )}
        </div>
        <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <p className="text-sm text-[#6B6B6B] max-w-xl">
            Get an email the moment a new litter of the breeds you're watching is registered.
            {!isNativeApp() && <> Part of Buyer Pass — $9/mo or $49/yr.</>}
            {isNativeApp() && <> Included with an active buyer plan.</>}
          </p>
          {!isNativeApp() && (
          <Link to="/billing">
            <Button className="bg-[#C9A227] hover:bg-[#C9A227]/90 text-ink rounded-full px-5" data-testid="breed-alerts-unlock-btn">
              <Heart size={16} className="mr-2" weight="fill" /> Unlock Buyer Pass
            </Button>
          </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="mb-8 bg-white border border-[#E5E5E5] rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.04)] overflow-hidden"
      data-testid="breed-alerts-card"
    >
      <div className="px-6 py-5 border-b border-[#E5E5E5] flex items-center gap-2">
        <BellRinging size={20} weight="duotone" className="text-gold" />
        <h2 className="font-spectral text-xl">Breed alerts</h2>
        <span className="text-[10px] bg-[#C9A227]/20 text-[#8a6f10] border border-[#C9A227]/40 rounded-full px-2 py-0.5 font-bold uppercase tracking-[0.15em]">
          Active
        </span>
      </div>
      <div className="px-6 py-5">
        <p className="text-sm text-[#6B6B6B] mb-4">
          Add the breeds you're actively hoping to find. We'll send a quick email (and
          drop a note in your inbox) whenever a matching litter is registered.
          Case-insensitive — "German Shepherd" and "german shepherd" count as one.
        </p>

        {/* Chips */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="breed-chips">
          {breeds.map((b) => (
            <span
              key={b}
              className="inline-flex items-center gap-1.5 bg-[#FBF7EC] border border-[#E8D9A6] text-[#3F3F3F] rounded-full pl-3 pr-1 py-1 text-sm"
              data-testid={`breed-chip-${b.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {b}
              <button
                type="button"
                onClick={() => removeBreed(b)}
                className="h-5 w-5 rounded-full hover:bg-[#E8D9A6] flex items-center justify-center text-[#6B6B6B] hover:text-ink"
                aria-label={`Remove ${b}`}
                data-testid={`breed-chip-remove-${b.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <CloseIcon size={12} weight="bold" />
              </button>
            </span>
          ))}
          {breeds.length === 0 && (
            <span className="text-xs text-[#9A9A9A] italic py-1">No breeds yet — add one below.</span>
          )}
        </div>

        {/* Autocomplete input */}
        <div className="relative">
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Type a breed (e.g. German Shepherd) and press Enter"
            data-testid="breed-alerts-input"
          />
          {matches.length > 0 && query && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-[#E5E5E5] rounded-lg shadow-lg overflow-hidden">
              {matches.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => addBreed(m)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#FBF7EC]"
                  data-testid={`breed-suggestion-${m.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {m}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full px-6"
            data-testid="breed-alerts-save-btn"
          >
            {saving ? "Saving…" : "Save breed alerts"}
          </Button>
        </div>
      </div>
    </div>
  );
}
